/**
 * Hub user adoption + trending people from login/message/referral caches.
 */

import { buildUserProfiles, profilesToArray } from '../../utils/userAnalytics';
import { DIRECTION_INBOUND, rowMatchesFacilityScope } from '../../utils/referralDirection';

function f(record) {
  return record?.data || record?.Data || {};
}

function facKey(id) {
  if (id == null || id === '') return '';
  const n = Number(id);
  if (Number.isFinite(n)) return String(Math.round(n));
  return String(id).trim();
}

function filterHubLogins(records, hubFacIds) {
  if (!Array.isArray(records)) return [];
  if (!hubFacIds?.size) return records;
  return records.filter(r => {
    const fid = facKey(f(r).facility_id);
    return fid && hubFacIds.has(fid);
  });
}

function filterHubMessages(records, hubFacIds) {
  return filterHubLogins(records, hubFacIds);
}

function filterHubReferrals(records, hubFacIds) {
  if (!Array.isArray(records)) return [];
  if (!hubFacIds?.size) return records;
  // Hub users appear on either side of inbound-scoped referrals.
  return records.filter(r => rowMatchesFacilityScope(f(r), 'referrals', hubFacIds, DIRECTION_INBOUND));
}

function isPhysician(userType) {
  const t = String(userType || '').toLowerCase();
  return /physician|doctor|md|do\b|provider|surgeon|cardiolog|radiolog/.test(t);
}

function daysSince(isoDay, asOfDay) {
  if (!isoDay) return null;
  const a = new Date(`${asOfDay}T00:00:00`);
  const d = new Date(`${isoDay}T00:00:00`);
  return Math.floor((a - d) / 86400000);
}

export function computeUsersTab({
  loginsCurrent,
  loginsPrior,
  messagesCurrent,
  referralsCurrent,
  hubFacIds,
  asOfDay,
}) {
  const logC = filterHubLogins(loginsCurrent, hubFacIds);
  const logP = filterHubLogins(loginsPrior, hubFacIds);
  const msgC = filterHubMessages(messagesCurrent, hubFacIds);
  const refC = filterHubReferrals(referralsCurrent, hubFacIds);

  const currentProfiles = profilesToArray(buildUserProfiles(logC, msgC, refC));
  const priorProfiles = profilesToArray(buildUserProfiles(logP, [], []));

  const priorById = new Map(priorProfiles.map(p => [String(p.userId), p]));

  const users = currentProfiles.length;
  const asOf = asOfDay || new Date().toISOString().slice(0, 10);

  // Active = logged in within last 60 days of asOf (or any login in current window with recent lastActive).
  let active = 0;
  let dormant = 0;
  let physicians = 0;
  let newUsers = 0;

  const scored = currentProfiles.map(p => {
    const prior = priorById.get(String(p.userId));
    const currLog = p.logins || 0;
    const prevLog = prior?.logins || 0;
    let pct = null;
    if (prevLog > 0) pct = ((currLog - prevLog) / prevLog) * 100;
    else if (currLog > 0) pct = 100;

    const days = daysSince(p.lastActiveDate, asOf);
    const isActive = days != null && days < 60;
    if (isActive) active += 1;
    else dormant += 1;
    if (isPhysician(p.userType)) physicians += 1;
    // "New" ≈ appeared in current window with no prior-window logins
    if (currLog > 0 && prevLog === 0) newUsers += 1;

    return {
      userId: p.userId,
      name: p.name || `User ${p.userId}`,
      role: p.userType || p.speciality || 'User',
      facility: p.facility || '',
      logins: currLog,
      priorLogins: prevLog,
      pct,
      lastActiveDate: p.lastActiveDate,
      daysSinceActive: days,
      isActive,
    };
  });

  const trendingUp = scored
    .filter(p => p.pct != null && p.pct > 5 && p.priorLogins > 0)
    .sort((a, b) => (b.pct ?? 0) - (a.pct ?? 0))
    .slice(0, 6);

  const trendingDown = scored
    .filter(p => (p.pct != null && p.pct < -5) || (!p.isActive && p.logins + p.priorLogins > 0))
    .sort((a, b) => (a.pct ?? -999) - (b.pct ?? -999))
    .slice(0, 6);

  const activePct = users ? active / users : 0;

  return {
    users,
    active,
    dormant,
    activePct,
    physicians,
    staff: Math.max(0, users - physicians),
    newUsers,
    trendingUp,
    trendingDown,
    // MFA / licensed seats unavailable
    mfaPct: null,
  };
}

export function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const AVATAR_COLORS = ['#0c5a64', '#1e3a8a', '#0f7a5c', '#4f46e5', '#b45309', '#be123c'];
